<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    loading-message="Loading Call Stats..."
    button-location="top"
    @submit="loadCallStats"
  >
    <template #title>
      Call Stats
    </template>

    <template #top-form>
      <div class="row justify-content-center">
        <el-radio-group v-model="period">
          <el-radio-button label="today">
            Today
          </el-radio-button>
          <el-radio-button label="last7Day">
            Last 7 day
          </el-radio-button>
          <el-radio-button label="last30Day">
            Last 30 day
          </el-radio-button>
          <el-radio-button label="lastFullMonth">
            Last Full Month
          </el-radio-button>
          <el-radio-button label="currentMonth">
            Current Month
          </el-radio-button>
          <el-radio-button label="customDates">
            Custom Dates
          </el-radio-button>
        </el-radio-group>
      </div>

      <div
        v-if="isCustomDates"
        class="row"
      >
        <label class="col-sm-3 control-label pt-10"> Date Range </label>
        <div class="col-sm-5">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
          >
            <template #comment>
              <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
            </template>
          </date-picker>
        </div>
        <div class="col-sm-3 col-12 form-submit-button-group left-button-group pt-5 mobile-pt-0">
          <p-button
            type="purple"
            round
            native-type="submit"
            data-test="btn-email-search"
            :disabled="isLoading"
          >
            Search
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </div>
    </template>

    <template #content>
      <hr>
      <div class="row">
        <label class="col-sm-4 col-6 control-label"> Total Calls: </label>
        <div class="col-sm-1 col-6 value">
          {{ callStat?.totalCalls ?? 0 }}
        </div>
      </div>

      <div class="row">
        <label class="col-sm-4 col-6 control-label"> Total Minutes with GoGo Operator: </label>
        <div class="col-sm-7 col-6 value">
          {{ util.formatSecondsDuration(callStat?.totalCallDuration ?? 0) || '0' }}
        </div>
      </div>

      <div class="row">
        <label class="col-sm-4 col-6 control-label"> Avg Hold Time: </label>
        <div class="col-sm-7 col-6 value">
          {{ util.formatSecondsDuration(callStat?.avgHoldTime ?? 0) || '0' }}
        </div>
      </div>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, watch, computed } from 'vue';
import Moment from 'moment-timezone';

import util from '@/Utils/util';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { usePartner } from '@/use/partner';
import { useSwalDialog } from '@/use/swalDialog';
import { partnerService } from '@/services/partners'
import { PartnerCallLogStat } from '@/models/partner'

const pageId = 'partner-call-stats'

const period = ref('today')
const customStartDate = ref('')
const customEndDate = ref('')
const isLoading = ref(false)
const timeSpanValue: Ref<any> = ref(null);
const callStat: Ref<PartnerCallLogStat | null> = ref(null)

const { partnerCode } = usePartner();
const { showErrorPopupDialog } = useSwalDialog();

const isCustomDates = computed(() => period.value === 'customDates')

const getDateRange = () => {
  let startDate = '';
  let endDate = Moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');

  switch(period.value) {
    case 'today':
      startDate = Moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');
      break;
    case 'last7Day':
      startDate = Moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss');
      break;
    case 'last30Day':
      startDate = Moment().subtract(30, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss');
      break;
    case 'lastFullMonth':
      startDate = Moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD HH:mm:ss');
      endDate = Moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss');
      break;
    case 'currentMonth':
      startDate = Moment().startOf('month').format('YYYY-MM-DD HH:mm:ss');
      break;
    case 'customDates':
      startDate = customStartDate.value;
      endDate = customEndDate.value;
      break;
    default:
      break;
  }

  return {
    startDate,
    endDate
  }
}

const loadCallStats = async () => {
  catchErrorMessageBox(async () => {
    isLoading.value = true;

    const { startDate, endDate } = getDateRange();

    if (!startDate || !endDate) {
      return;
    }

    callStat.value = await partnerService.getPartnerCallLogStats({
      partnerCodeName: partnerCode.value,
      startDate,
      endDate
    })
  })
    .finally(() => (isLoading.value = false))
    .catch((error) => {
      const errorText = util.getErrorResponse(error, false);
      showErrorPopupDialog({
        text: errorText,
      });
    });
}

const resetForm = () => {
  timeSpanValue.value = null
}

watch(timeSpanValue, (value: any) => {
  if (value) {
    customStartDate.value = util.formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss');
    customEndDate.value = util.formatDateTime(Moment(value[1]).endOf('day'), 'YYYY-MM-DD HH:mm:ss');
  } else {
    customStartDate.value = '';
    customEndDate.value = '';
  }
});

watch(period, () => {
  if (isCustomDates.value) {
    return
  }

  loadCallStats()
}, {
  immediate: true
})
</script>

<style lang="scss">
#partner-call-stats {
  .control-label {
    padding-bottom: 10px;

    & + div.value {
      color: #ee682d;
      font-weight: bold;
    }
  }
}
</style>